import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { X } from 'lucide-react';
import { Button } from "@/components/ui/button";
import Loading from './Loading';

// Types
interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface VerificationResponse {
  success: boolean;
  message?: string;
  redirectUrl?: string;
}

// Constants
const WORKER_URL = 'https://demo-redirect.striveus.workers.dev/';
const CODE_LENGTH = 6;
const INITIAL_CODE = Array(CODE_LENGTH).fill('');

// Memoized input component for better performance
const CodeInput = memo(({ 
  value, 
  onChange, 
  onKeyDown, 
  inputRef, 
  index 
}: { 
  value: string;
  onChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  inputRef: (el: HTMLInputElement | null) => void;
  index: number;
}) => (
  <input
    ref={inputRef}
    type="text"
    maxLength={1}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    onKeyDown={onKeyDown}
    className="w-10 h-12 sm:w-12 sm:h-14 text-center text-xl font-semibold border-2 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all uppercase"
    style={{ appearance: 'textfield' }}
    autoCapitalize="characters"
    aria-label={`Code digit ${index + 1}`}
  />
));

CodeInput.displayName = 'CodeInput';

// Main component
const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const [code, setCode] = useState<string[]>(INITIAL_CODE);
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [hasChecked, setHasChecked] = useState<boolean>(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const modalRef = useRef<HTMLDivElement>(null);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setCode(INITIAL_CODE);
      setError('');
      setIsLoading(false);
      setHasChecked(false);
    } else if (isOpen && !hasChecked) {
      setIsChecking(false);
      setHasChecked(true);
    }
  }, [isOpen, hasChecked]);

  // Focus first input when modal opens
  useEffect(() => {
    if (isOpen && !isChecking && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [isOpen, isChecking]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setCode(INITIAL_CODE);
    setError('');
    onClose();
  }, [onClose]);

  const handleInputChange = useCallback((index: number, value: string) => {
    // Convert to uppercase and remove any non-alphanumeric characters
    value = value.toUpperCase().replace(/[^A-Z0-9]/g, '');

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError('');

    // Auto-advance to next input
    if (value && index < CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all characters are entered
    if (index === CODE_LENGTH - 1 && value && !newCode.includes('')) {
      handleSubmit(newCode);
    }
  }, [code]);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      // Move to previous input on backspace if current input is empty
      inputRefs.current[index - 1]?.focus();
    }
  }, [code]);

  const handleSubmit = useCallback(async (codeToSubmit = code) => {
    const verificationCode = codeToSubmit.join('');
    
    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(WORKER_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ code: verificationCode }),
        credentials: 'include'
      });

      const data: VerificationResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Invalid code');
      }

      if (data.success === true && data.redirectUrl) {
        window.open(data.redirectUrl, '_blank');
        handleClose();
      }
    } catch (err) {
      setError('Invalid verification code');
      setCode(INITIAL_CODE);
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  }, [code, handleClose]);

  // Don't render anything if not open
  if (!isOpen) return null;
  
  // Don't render content while checking auth
  if (isChecking) {
    return (
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
        role="dialog"
        aria-modal="true"
        aria-label="Authentication check"
      >
        <div className="bg-white rounded-xl shadow-xl p-6 flex items-center justify-center">
          <Loading size="medium" fullScreen={false} />
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={modalRef}
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-label="Verification code input"
    >
      <div className="bg-gradient-to-b from-white to-slate-50 rounded-xl shadow-xl max-w-md w-full">
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 rounded-t-xl p-4 sm:p-6 flex justify-between items-center">
          <div className="flex-1 text-center">
            <h2 className="text-xl sm:text-2xl font-bold text-white">Welcome to Strive</h2>
          </div>
          <button 
            onClick={handleClose}
            className="p-2 rounded-full hover:bg-blue-500/20 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </button>
        </div>
        
        <div className="p-4 sm:p-8">
          <div className="text-center space-y-4 mb-8">
            <p className="text-lg text-gray-900 font-medium">
              We're glad you're here
            </p>
            <p className="text-gray-600">
              Please enter your customer PIN
            </p>
          </div>

          <div 
            className="flex justify-center space-x-2 sm:space-x-4 mb-6"
            role="group"
            aria-label="Customer PIN input"
          >
            {code.map((digit, index) => (
              <CodeInput
                key={index}
                value={digit}
                onChange={(value) => handleInputChange(index, value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                inputRef={(el) => {
                  inputRefs.current[index] = el;
                }}
                index={index}
              />
            ))}
          </div>

          <div className="min-h-[76px] mb-6">
            {error && (
              <div 
                className="p-3 bg-red-50 border border-red-200 rounded-lg"
                role="alert"
                aria-live="polite"
              >
                <div className="flex items-start space-x-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-center">
            <Button
              onClick={() => handleSubmit()}
              disabled={isLoading || code.includes('')}
              className={`w-full sm:w-auto px-8 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg shadow-sm hover:from-blue-700 hover:to-blue-800 hover:shadow-md hover:translate-y-[-2px] transition-all duration-300 ease-in-out border-0 ${
                isLoading ? 'opacity-75 cursor-not-allowed' : ''
              }`}
              aria-label={isLoading ? 'Verifying code' : 'Submit verification code'}
            >
              {isLoading ? 'Verifying...' : 'Submit'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(LoginModal); 